<template>
  <div class="payments-layout">
    <!-- Sidebar -->
    <Sidebar />

    <!-- Main Content -->
    <div class="main-content">
      <!-- Top Header -->
      <TopHeader />

      <!-- Page Content -->
      <main class="page-content">
        <div class="payments-view">
          <!-- Page Header -->
          <div class="page-header">
            <div class="header-content">
              <h1 class="page-title">💳 Payments & Billing</h1>
              <p class="page-subtitle">Manage your subscription and payment history</p>
            </div>
            <div class="header-stats">
              <div class="stat-card">
                <div class="stat-number">{{ paymentHistory.length }}</div>
                <div class="stat-label">Total Payments</div>
              </div>
            </div>
          </div>

          <!-- Subscription Status -->
          <div class="subscription-section">
            <div class="section-header">
              <h2 class="section-title">Current Subscription</h2>
            </div>

            <div v-if="loadingSubscription" class="loading-container">
              <div class="loading-spinner"></div>
              <p>Loading subscription status...</p>
            </div>

            <div v-else-if="subscriptionError" class="error-container">
              <div class="error-icon">⚠️</div>
              <h3>Error Loading Subscription</h3>
              <p>{{ subscriptionError }}</p>
              <button @click="loadSubscriptionStatus" class="retry-button">Try Again</button>
            </div>

            <div v-else-if="subscription" class="subscription-card">
              <div class="subscription-info">
                <div class="plan-badge">
                  <span class="plan-name">{{ subscription.plan_name }}</span>
                  <span class="plan-status" :class="{ 'active': !subscription.is_time_to_renew, 'renewal-due': subscription.is_time_to_renew }">
                    {{ subscription.is_time_to_renew ? 'Renewal Due' : 'Active' }}
                  </span>
                </div>
                <div class="subscription-details">
                  <div class="detail-item">
                    <span class="detail-label">📅 Start Date:</span>
                    <span class="detail-value">{{ formatDate(subscription.start_date) }}</span>
                  </div>
                  <div class="detail-item">
                    <span class="detail-label">📆 End Date:</span>
                    <span class="detail-value">{{ formatDate(subscription.end_date) }}</span>
                  </div>
                  <div class="detail-item">
                    <span class="detail-label">⏰ Days to Renewal:</span>
                    <span class="detail-value">{{ subscription.days_to_renewal }} days</span>
                  </div>
                </div>
              </div>

              <div class="renewal-actions" v-if="subscription && (subscription.is_time_to_renew || subscription.days_to_renewal <= 30 || true)">
                <button @click="navigateToRenewPage" class="renew-button">
                  🔄 Renew Subscription
                </button>
              </div>
            </div>
          </div>

          <!-- Payment History -->
          <div class="history-section">
            <div class="section-header">
              <h2 class="section-title">Payment History</h2>
              <button @click="loadPaymentHistory" class="refresh-button">
                🔄 Refresh
              </button>
            </div>

            <div v-if="loadingHistory" class="loading-container">
              <div class="loading-spinner"></div>
              <p>Loading payment history...</p>
            </div>

            <div v-else-if="historyError" class="error-container">
              <div class="error-icon">⚠️</div>
              <h3>Error Loading Payment History</h3>
              <p>{{ historyError }}</p>
              <button @click="loadPaymentHistory" class="retry-button">Try Again</button>
            </div>

            <div v-else-if="paymentHistory.length === 0" class="empty-state">
              <div class="empty-icon">💳</div>
              <h3>No Payment History</h3>
              <p>You don't have any payment records yet.</p>
            </div>

            <div v-else class="history-container">
              <!-- Desktop Table View -->
              <div class="table-wrapper desktop-view">
                <table class="payments-table">
                  <thead>
                    <tr>
                      <th>Payment ID</th>
                      <th>Plan</th>
                      <th>Amount</th>
                      <th>Payment Date</th>
                      <th>Subscription Period</th>
                      <th>Next Payment</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="payment in paymentHistory" :key="payment.id" class="payment-row">
                      <td class="id-cell">
                        <span class="payment-id">#{{ payment.id }}</span>
                      </td>
                      <td class="plan-cell">
                        <div class="plan-info">
                          <span class="plan-name">{{ payment.plan_name }}</span>
                          <span class="plan-id">ID: {{ payment.plan_id }}</span>
                        </div>
                      </td>
                      <td class="amount-cell">
                        <span class="amount">{{ payment.amount }} EGP</span>
                      </td>
                      <td class="date-cell">
                        <span class="date">{{ formatDate(payment.created_at) }}</span>
                      </td>
                      <td class="period-cell">
                        <div class="period-info">
                          <span class="period">{{ formatDate(payment.start_date) }} - {{ formatDate(payment.end_date) }}</span>
                        </div>
                      </td>
                      <td class="next-payment-cell">
                        <span class="next-date">{{ formatDate(payment.next_payment_date) }}</span>
                      </td>
                      <td class="status-cell">
                        <span class="status-badge" :class="payment.subscription_status.toLowerCase()">
                          {{ payment.subscription_status }}
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <!-- Mobile Cards View -->
              <div class="mobile-view">
                <div v-for="payment in paymentHistory" :key="payment.id" class="payment-card">
                  <div class="payment-card-header">
                    <div class="payment-info">
                      <h3 class="payment-title">{{ payment.plan_name }}</h3>
                      <span class="payment-id">#{{ payment.id }}</span>
                    </div>
                    <div class="payment-amount">{{ payment.amount }} EGP</div>
                  </div>

                  <div class="payment-card-body">
                    <div class="payment-info-row">
                      <span class="info-label">📅 Payment Date:</span>
                      <span class="info-value">{{ formatDate(payment.created_at) }}</span>
                    </div>

                    <div class="payment-info-row">
                      <span class="info-label">📆 Period:</span>
                      <span class="info-value">{{ formatDate(payment.start_date) }} - {{ formatDate(payment.end_date) }}</span>
                    </div>

                    <div class="payment-info-row">
                      <span class="info-label">🔄 Next Payment:</span>
                      <span class="info-value">{{ formatDate(payment.next_payment_date) }}</span>
                    </div>

                    <div class="payment-info-row">
                      <span class="info-label">📊 Status:</span>
                      <span class="status-badge" :class="payment.subscription_status.toLowerCase()">
                        {{ payment.subscription_status }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>

    <!-- Renewal Modal -->
    <div v-if="showRenewalModal" class="modal-overlay" @click="closeRenewalModal">
      <div class="modal-container" @click.stop>
        <div class="modal-header">
          <h3 class="modal-title">Renew Subscription</h3>
          <button @click="closeRenewalModal" class="modal-close">×</button>
        </div>

        <div class="modal-body">
          <div v-if="subscription" class="renewal-summary">
            <h4>Subscription Details</h4>
            <div class="summary-item">
              <span class="summary-label">Plan:</span>
              <span class="summary-value">{{ subscription.plan_name }}</span>
            </div>
            <div class="summary-item">
              <span class="summary-label">Current End Date:</span>
              <span class="summary-value">{{ formatDate(subscription.end_date) }}</span>
            </div>
            <div class="summary-item">
              <span class="summary-label">Days Remaining:</span>
              <span class="summary-value">{{ subscription.days_to_renewal }} days</span>
            </div>
          </div>

          <form @submit.prevent="handleRenewal" class="renewal-form">
            <div class="form-section-title">Plan Selection</div>

            <div v-if="loadingPlans" class="loading-container">
              <div class="loading-spinner"></div>
              <p>Loading available plans...</p>
            </div>

            <div v-else-if="plansError" class="error-container">
              <div class="error-icon">⚠️</div>
              <h3>Error Loading Plans</h3>
              <p>{{ plansError }}</p>
              <button @click="loadAvailablePlans" class="retry-button">Try Again</button>
            </div>

            <div v-else class="form-group">
              <label for="renewalPlanId" class="form-label">Select Plan</label>
              <select
                id="renewalPlanId"
                v-model="renewalForm.planId"
                class="form-input"
                :class="{ 'error': renewalErrors.planId }"
                required
              >
                <option value="">Choose a plan...</option>
                <option v-for="plan in availablePlans" :key="plan.id" :value="plan.id">
                  {{ plan.name }} - {{ plan.price }} EGP ({{ plan.agents_limit }} agents, {{ plan.credit_amount }} credits)
                </option>
              </select>
              <span v-if="renewalErrors.planId" class="error-message">{{ renewalErrors.planId }}</span>
            </div>

            <div class="form-section-title">Payment Information</div>

            <div class="form-group">
              <label for="renewalCardNumber" class="form-label">Card Number</label>
              <div class="input-wrapper">
                <svg class="input-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/>
                  <line x1="1" y1="10" x2="23" y2="10"/>
                </svg>
                <input
                  id="renewalCardNumber"
                  v-model="renewalForm.cardNumber"
                  type="text"
                  placeholder="1234 5678 9012 3456"
                  class="form-input"
                  :class="{ 'error': renewalErrors.cardNumber }"
                  maxlength="19"
                  @input="formatCardNumber"
                  required
                />
              </div>
              <span v-if="renewalErrors.cardNumber" class="error-message">{{ renewalErrors.cardNumber }}</span>
            </div>

            <div class="form-row">
              <div class="form-group">
                <label for="renewalCardMonth" class="form-label">Expiry Month</label>
                <select
                  id="renewalCardMonth"
                  v-model="renewalForm.expiryMonth"
                  class="form-input"
                  :class="{ 'error': renewalErrors.expiryMonth }"
                  required
                >
                  <option value="">Month</option>
                  <option v-for="month in months" :key="month.value" :value="month.value">
                    {{ month.label }}
                  </option>
                </select>
                <span v-if="renewalErrors.expiryMonth" class="error-message">{{ renewalErrors.expiryMonth }}</span>
              </div>

              <div class="form-group">
                <label for="renewalCardYear" class="form-label">Expiry Year</label>
                <select
                  id="renewalCardYear"
                  v-model="renewalForm.expiryYear"
                  class="form-input"
                  :class="{ 'error': renewalErrors.expiryYear }"
                  required
                >
                  <option value="">Year</option>
                  <option v-for="year in years" :key="year" :value="year">
                    {{ year }}
                  </option>
                </select>
                <span v-if="renewalErrors.expiryYear" class="error-message">{{ renewalErrors.expiryYear }}</span>
              </div>

              <div class="form-group">
                <label for="renewalCardCvv" class="form-label">CVV</label>
                <input
                  id="renewalCardCvv"
                  v-model="renewalForm.cvv"
                  type="password"
                  placeholder="123"
                  class="form-input"
                  :class="{ 'error': renewalErrors.cvv }"
                  maxlength="4"
                  required
                />
                <span v-if="renewalErrors.cvv" class="error-message">{{ renewalErrors.cvv }}</span>
              </div>
            </div>

            <div v-if="renewalError" class="renewal-error">
              {{ renewalError }}
            </div>
          </form>
        </div>

        <div class="modal-footer">
          <button @click="closeRenewalModal" class="modal-button secondary" :disabled="isRenewing">
            Cancel
          </button>
          <button @click="handleRenewal" class="modal-button primary" :disabled="isRenewing || !isRenewalFormValid">
            <span v-if="!isRenewing">Proceed to Payment</span>
            <span v-else class="loading-spinner">
              <span class="spinner"></span>
              Processing...
            </span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Sidebar from '../components/Sidebar.vue'
import TopHeader from '../components/TopHeader.vue'
import { PaymentsService } from '../services/payments.js'

export default {
  name: 'PaymentsView',
  components: {
    Sidebar,
    TopHeader
  },
  data () {
    return {
      paymentHistory: [],
      subscription: null,
      availablePlans: [],
      loadingHistory: false,
      loadingSubscription: false,
      loadingPlans: false,
      historyError: null,
      subscriptionError: null,
      plansError: null,
      showRenewalModal: false,
      renewalForm: {
        cardNumber: '',
        expiryMonth: '',
        expiryYear: '',
        cvv: '',
        planId: null
      },
      renewalErrors: {},
      renewalError: '',
      isRenewing: false,
      months: [
        { value: '01', label: '01 - January' },
        { value: '02', label: '02 - February' },
        { value: '03', label: '03 - March' },
        { value: '04', label: '04 - April' },
        { value: '05', label: '05 - May' },
        { value: '06', label: '06 - June' },
        { value: '07', label: '07 - July' },
        { value: '08', label: '08 - August' },
        { value: '09', label: '09 - September' },
        { value: '10', label: '10 - October' },
        { value: '11', label: '11 - November' },
        { value: '12', label: '12 - December' }
      ]
    }
  },
  computed: {
    years () {
      const currentYear = new Date().getFullYear()
      const years = []
      for (let i = 0; i < 15; i++) {
        years.push((currentYear + i).toString())
      }
      return years
    },
    isRenewalFormValid () {
      const form = this.renewalForm
      return form.planId &&
             form.cardNumber.trim() &&
             form.cardNumber.replace(/\s/g, '').length >= 13 &&
             form.expiryMonth &&
             form.expiryYear &&
             form.cvv &&
             form.cvv.length >= 3 && form.cvv.length <= 4
    }
  },
  async mounted () {
    await Promise.all([
      this.loadPaymentHistory(),
      this.loadSubscriptionStatus(),
      this.loadAvailablePlans()
    ])
  },
  methods: {
    async loadPaymentHistory () {
      this.loadingHistory = true
      this.historyError = null

      try {
        const result = await PaymentsService.getPaymentHistory()
        console.log('Payment history response:', result)

        if (!result.success) {
          throw new Error(result.message || 'Failed to load payment history')
        }

        this.paymentHistory = result.payments || []
      } catch (error) {
        console.error('Error loading payment history:', error)
        this.historyError = error.message || 'Failed to load payment history. Please try again.'
      } finally {
        this.loadingHistory = false
      }
    },

    async loadSubscriptionStatus () {
      this.loadingSubscription = true
      this.subscriptionError = null

      try {
        const result = await PaymentsService.getSubscriptionStatus()
        console.log('Subscription status response:', result)

        if (!result.success) {
          throw new Error(result.message || 'Failed to load subscription status')
        }

        this.subscription = result.subscription
      } catch (error) {
        console.error('Error loading subscription status:', error)
        this.subscriptionError = error.message || 'Failed to load subscription status. Please try again.'
      } finally {
        this.loadingSubscription = false
      }
    },

    async loadAvailablePlans () {
      this.loadingPlans = true
      this.plansError = null

      try {
        const result = await PaymentsService.getAvailablePlans()
        console.log('Available plans response:', result)

        if (!result.success) {
          throw new Error(result.message || 'Failed to load available plans')
        }

        this.availablePlans = result.plans
      } catch (error) {
        console.error('Error loading available plans:', error)
        this.plansError = error.message || 'Failed to load available plans. Please try again.'
      } finally {
        this.loadingPlans = false
      }
    },

    formatDate (dateString) {
      if (!dateString) return 'Not provided'
      try {
        const date = new Date(dateString)
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        })
      } catch (error) {
        return dateString
      }
    },

    formatCardNumber () {
      let value = this.renewalForm.cardNumber.replace(/\D/g, '')
      value = value.replace(/(\d{4})(?=\d)/g, '$1 ')
      this.renewalForm.cardNumber = value
    },

    validateRenewalForm () {
      this.renewalErrors = {}
      let isValid = true

      // Plan selection validation
      if (!this.renewalForm.planId) {
        this.renewalErrors.planId = 'Please select a plan'
        isValid = false
      }

      // Card number validation
      if (!this.renewalForm.cardNumber.trim()) {
        this.renewalErrors.cardNumber = 'Card number is required'
        isValid = false
      } else if (this.renewalForm.cardNumber.replace(/\s/g, '').length < 13) {
        this.renewalErrors.cardNumber = 'Please enter a valid card number'
        isValid = false
      }

      // Expiry month validation
      if (!this.renewalForm.expiryMonth) {
        this.renewalErrors.expiryMonth = 'Expiry month is required'
        isValid = false
      }

      // Expiry year validation
      if (!this.renewalForm.expiryYear) {
        this.renewalErrors.expiryYear = 'Expiry year is required'
        isValid = false
      }

      // CVV validation
      if (!this.renewalForm.cvv) {
        this.renewalErrors.cvv = 'CVV is required'
        isValid = false
      } else if (this.renewalForm.cvv.length < 3 || this.renewalForm.cvv.length > 4) {
        this.renewalErrors.cvv = 'CVV must be 3-4 digits'
        isValid = false
      }

      return isValid
    },

    async handleRenewal () {
      this.renewalError = ''

      if (!this.validateRenewalForm()) {
        return
      }

      this.isRenewing = true

      try {
        // Find selected plan to get amount
        const selectedPlan = this.availablePlans.find(plan => plan.id === this.renewalForm.planId)
        const amount = selectedPlan ? selectedPlan.price.toString() : '0'
        const renewalData = {
          cardNumber: this.renewalForm.cardNumber.replace(/\s/g, ''),
          expiryYear: this.renewalForm.expiryYear.slice(-2), // Convert 2027 to 27
          expiryMonth: this.renewalForm.expiryMonth,
          cvv: this.renewalForm.cvv,
          plan_id: this.renewalForm.planId,
          amount: amount
        }

        console.log('Sending renewal data:', renewalData)

        const result = await PaymentsService.initiateRenewal(renewalData)
        console.log('Renewal response:', result)

        if (!result.success) {
          throw new Error(result.message || 'Renewal failed')
        }

        if (result.redirectUrl && result.merchantRefNumber) {
          // Store the merchant reference number for payment status checking
          localStorage.setItem('merchantRefNum', result.merchantRefNumber)

          console.log('Using merchant reference number:', result.merchantRefNumber)

          // Redirect to payment page with iframe
          this.$router.push({
            name: 'payment',
            query: {
              url: result.redirectUrl,
              merchantRefNum: result.merchantRefNumber,
              renewal: 'true'
            }
          })
        } else {
          throw new Error('No payment URL or merchant reference number received')
        }
      } catch (error) {
        console.error('Renewal error:', error)
        this.renewalError = error.message || 'Renewal failed. Please try again.'
      } finally {
        this.isRenewing = false
      }
    },

    navigateToRenewPage () {
      this.$router.push({ name: 'renew-subscription' })
    }
  }
}
</script>

<style scoped>
/* Layout Structure */
.payments-layout {
  display: flex;
  min-height: 100vh;
  background: var(--bg-secondary);
  transition: background-color 0.3s ease;
}

.main-content {
  flex: 1;
  margin-left: 280px;
  display: flex;
  flex-direction: column;
}

.page-content {
  flex: 1;
  padding: 20px;
}

.payments-view {
  background: var(--bg-secondary);
  max-width: 1200px;
  margin: 0 auto;
}

/* Page Header */
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding: 20px 24px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 12px;
  color: white;
  box-shadow: 0 4px 20px rgba(102, 126, 234, 0.15);
}

.header-content h1 {
  font-size: 24px;
  font-weight: 700;
  color: white;
  margin: 0 0 4px;
}

.header-content p {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.8);
  margin: 0;
}

.header-stats {
  display: flex;
  gap: 12px;
}

.stat-card {
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(10px);
  padding: 12px 16px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  text-align: center;
  min-width: 80px;
}

.stat-number {
  font-size: 20px;
  font-weight: 700;
  color: white;
  margin-bottom: 2px;
}

.stat-label {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.8);
  font-weight: 500;
}

/* Section Styles */
.subscription-section,
.history-section {
  background: var(--bg-primary);
  border-radius: 12px;
  border: 1px solid var(--border-primary);
  margin-bottom: 16px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid var(--border-primary);
  background: var(--bg-tertiary);
}

.section-title {
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.section-title::before {
  content: '💳';
  font-size: 16px;
}

.history-section .section-title::before {
  content: '📊';
}

.refresh-button {
  background: var(--bg-secondary);
  color: var(--text-primary);
  border: 1px solid var(--border-primary);
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 4px;
}

.refresh-button:hover {
  background: var(--bg-tertiary);
  border-color: var(--border-secondary);
}

/* Subscription Card */
.subscription-card {
  padding: 20px;
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 20px;
  align-items: start;
}

.subscription-info {
  flex: 1;
}

.plan-badge {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
}

.plan-name {
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
}

.plan-status {
  padding: 3px 8px;
  border-radius: 12px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.plan-status.active {
  background: rgba(16, 185, 129, 0.15);
  color: #059669;
}

.plan-status.renewal-due {
  background: rgba(245, 158, 11, 0.15);
  color: #d97706;
}

.subscription-details {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 12px;
}

.detail-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.detail-label {
  font-size: 12px;
  font-weight: 500;
  color: var(--text-secondary);
}

.detail-value {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
}

.renewal-actions {
  display: flex;
  align-items: center;
}

.renew-button {
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  color: white;
  border: none;
  padding: 10px 16px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.renew-button:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
}

/* Loading and Error States */
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  text-align: center;
}

.loading-spinner {
  width: 32px;
  height: 32px;
  border: 2px solid var(--border-primary);
  border-top: 2px solid #667eea;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 12px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.loading-container p {
  font-size: 14px;
  color: var(--text-secondary);
  margin: 0;
}

.error-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  text-align: center;
}

.error-icon {
  font-size: 32px;
  margin-bottom: 12px;
}

.error-container h3 {
  color: var(--text-primary);
  margin: 0 0 6px;
  font-size: 16px;
}

.error-container p {
  color: var(--text-secondary);
  margin: 0 0 16px;
  font-size: 14px;
}

.retry-button {
  background: #667eea;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s ease;
}

.retry-button:hover {
  background: #5a67d8;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  text-align: center;
}

.empty-icon {
  font-size: 32px;
  margin-bottom: 12px;
  opacity: 0.6;
}

.empty-state h3 {
  color: var(--text-primary);
  margin: 0 0 6px;
  font-size: 16px;
}

.empty-state p {
  color: var(--text-secondary);
  margin: 0;
  font-size: 14px;
  max-width: 300px;
}

/* Table Styles */
.history-container {
  padding: 20px;
}

.desktop-view {
  display: block;
}

.mobile-view {
  display: none;
}

.table-wrapper {
  overflow-x: auto;
}

.payments-table {
  width: 100%;
  border-collapse: collapse;
}

.payments-table th {
  background: var(--bg-tertiary);
  color: var(--text-primary);
  font-weight: 600;
  font-size: 12px;
  padding: 12px 8px;
  text-align: left;
  border-bottom: 1px solid var(--border-primary);
  white-space: nowrap;
}

.payments-table td {
  padding: 12px 8px;
  border-bottom: 1px solid var(--border-primary);
  color: var(--text-primary);
  font-size: 13px;
  vertical-align: top;
}

.payment-row:hover {
  background: var(--bg-tertiary);
}

.id-cell .payment-id {
  font-weight: 600;
  color: var(--text-primary);
}

.plan-cell .plan-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.plan-cell .plan-name {
  font-weight: 600;
  color: var(--text-primary);
}

.plan-cell .plan-id {
  font-size: 12px;
  color: var(--text-secondary);
}

.amount-cell .amount {
  font-weight: 600;
  font-size: 16px;
  color: var(--text-primary);
}

.status-badge {
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
}

.status-badge.active {
  background: rgba(16, 185, 129, 0.1);
  color: #059669;
}

.status-badge.expired {
  background: rgba(239, 68, 68, 0.1);
  color: #dc2626;
}

/* Mobile Cards */
.payment-card {
  background: var(--bg-primary);
  border: 1px solid var(--border-primary);
  border-radius: 8px;
  margin-bottom: 12px;
  overflow: hidden;
  transition: all 0.2s ease;
}

.payment-card:hover {
  border-color: var(--border-secondary);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.payment-card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 12px 14px;
  border-bottom: 1px solid var(--border-primary);
  background: var(--bg-tertiary);
}

.payment-info {
  flex: 1;
}

.payment-title {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 2px;
}

.payment-id {
  font-size: 11px;
  color: var(--text-secondary);
  font-weight: 500;
}

.payment-amount {
  font-size: 16px;
  font-weight: 700;
  color: var(--text-primary);
}

.payment-card-body {
  padding: 12px 14px;
}

.payment-info-row {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 8px;
  padding: 2px 0;
}

.payment-info-row:last-child {
  margin-bottom: 0;
}

.info-label {
  font-size: 12px;
  font-weight: 500;
  color: var(--text-secondary);
  min-width: 100px;
  flex-shrink: 0;
}

.info-value {
  font-size: 12px;
  color: var(--text-primary);
  word-break: break-word;
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}

.modal-container {
  background: var(--bg-primary);
  border-radius: 16px;
  border: 1px solid var(--border-primary);
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px;
  border-bottom: 1px solid var(--border-primary);
}

.modal-title {
  margin: 0;
  color: var(--text-primary);
  font-size: 18px;
  font-weight: 600;
}

.modal-close {
  background: none;
  border: none;
  font-size: 20px;
  cursor: pointer;
  color: var(--text-secondary);
  padding: 4px;
  border-radius: 4px;
  transition: all 0.2s ease;
}

.modal-close:hover {
  background: var(--bg-tertiary);
  color: var(--text-primary);
}

.modal-body {
  padding: 24px;
}

.renewal-summary {
  background: var(--bg-secondary);
  padding: 16px;
  border-radius: 8px;
  margin-bottom: 24px;
}

.renewal-summary h4 {
  color: var(--text-primary);
  margin: 0 0 12px;
  font-size: 16px;
  font-weight: 600;
}

.summary-item {
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
}

.summary-label {
  color: var(--text-secondary);
  font-size: 14px;
}

.summary-value {
  color: var(--text-primary);
  font-weight: 500;
  font-size: 14px;
}

/* Form Styles */
.form-section-title {
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 16px 0;
  padding-bottom: 8px;
  border-bottom: 1px solid var(--border-primary);
}

.form-group {
  margin-bottom: 20px;
}

.form-label {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 6px;
}

.input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.input-icon {
  position: absolute;
  left: 16px;
  width: 18px;
  height: 18px;
  opacity: 0.6;
  z-index: 2;
  color: var(--text-secondary);
}

.form-input {
  width: 100%;
  padding: 12px 16px 12px 52px;
  border: 2px solid var(--border-primary);
  border-radius: 8px;
  font-size: 14px;
  background: var(--bg-primary);
  color: var(--text-primary);
  transition: all 0.2s;
  outline: none;
}

.form-input:focus {
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.form-input.error {
  border-color: #ef4444;
  background: #fef2f2;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 16px;
}

.error-message {
  display: block;
  color: #ef4444;
  font-size: 12px;
  margin-top: 4px;
}

.renewal-error {
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #dc2626;
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 14px;
  margin-top: 16px;
  text-align: center;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 24px;
  border-top: 1px solid var(--border-primary);
}

.modal-button {
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  transition: all 0.2s;
  position: relative;
}

.modal-button.secondary {
  background: var(--bg-secondary);
  color: var(--text-primary);
  border: 1px solid var(--border-primary);
}

.modal-button.secondary:hover:not(:disabled) {
  background: var(--bg-tertiary);
  border-color: var(--border-secondary);
}

.modal-button.primary {
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  color: white;
}

.modal-button.primary:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
}

.modal-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.modal-button.primary:disabled {
  background: #9ca3af;
}

.loading-spinner {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

/* Responsive Design */
@media (max-width: 1024px) {
  .page-header {
    flex-direction: column;
    gap: 20px;
    align-items: flex-start;
  }

  .header-stats {
    align-self: flex-end;
  }

  .subscription-card {
    flex-direction: column;
    gap: 20px;
  }

  .renewal-actions {
    align-self: flex-start;
  }
}

@media (max-width: 768px) {
  .main-content {
    margin-left: 0;
    padding-top: 70px;
  }

  .page-content {
    padding: 12px;
  }

  .payments-view {
    padding: 0;
  }

  .page-header {
    flex-direction: column;
    gap: 12px;
    padding: 16px;
    margin-bottom: 12px;
  }

  .header-content h1 {
    font-size: 20px;
  }

  .header-content p {
    font-size: 13px;
  }

  .header-stats {
    align-self: stretch;
    justify-content: center;
  }

  .subscription-section,
  .history-section {
    margin-bottom: 12px;
  }

  .section-header {
    padding: 12px 16px;
  }

  .section-title {
    font-size: 14px;
  }

  .refresh-button {
    padding: 4px 8px;
    font-size: 11px;
  }

  .subscription-card {
    padding: 16px;
    grid-template-columns: 1fr;
    gap: 12px;
  }

  .subscription-details {
    grid-template-columns: repeat(2, 1fr);
    gap: 8px;
  }

  .detail-item {
    gap: 1px;
  }

  .detail-label {
    font-size: 11px;
  }

  .detail-value {
    font-size: 13px;
  }

  .renewal-actions {
    justify-content: center;
  }

  /* Hide desktop table, show mobile cards */
  .desktop-view {
    display: none;
  }

  .mobile-view {
    display: block;
  }

  .history-container {
    padding: 12px;
  }

  .modal-container {
    margin: 10px;
    max-height: calc(100vh - 20px);
  }

  .modal-header {
    padding: 16px;
  }

  .modal-header h3 {
    font-size: 16px;
  }

  .modal-body {
    padding: 16px;
  }

  .modal-footer {
    padding: 16px;
    flex-direction: column;
    gap: 8px;
  }

  .modal-button {
    width: 100%;
    padding: 12px;
    font-size: 16px;
  }

  .form-row {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  .subscription-details {
    grid-template-columns: 1fr;
    gap: 12px;
  }
}

@media (max-width: 480px) {
  .page-content {
    padding: 8px;
  }

  .page-header {
    padding: 12px;
    margin-bottom: 12px;
  }

  .header-content h1 {
    font-size: 20px;
  }

  .section-header {
    padding: 12px;
  }

  .subscription-card {
    padding: 12px;
  }

  .history-container {
    padding: 8px;
  }

  .payment-card-header {
    padding: 12px;
  }

  .payment-card-body {
    padding: 12px;
  }

  .info-label {
    font-size: 13px;
    min-width: 100px;
  }

  .info-value {
    font-size: 13px;
  }
}
</style>
